import React, { useEffect, useMemo, useState } from 'react';
import { researchosService } from '../../services/researchosService';
import { useSortableRows, SortableTh } from '../../components/tables/SortableTable';

// Courier — the Research OS daily mailer's read surface (superuser-only; the backend gates with
// IsResearchOSAdmin). The producer (the Mac mini's scheduled Claude runs) POSTs pre-rendered HTML briefs
// with its IngestToken; Courier stores each one and emails it on arrival to the PLATFORM-DEFINED recipient
// group for its type (BriefType.recipient_users — the payload's list is audit-only; a mismatch shows as the
// "override" badge). This page shows what arrived, whether it went, to whom and when — and renders the stored
// HTML in a sandboxed iframe via srcDoc (fetched with the API token; the HTML endpoint also sets a no-script CSP).
// Nothing here writes: ingest is the producer's token-only POST, and a sent brief is frozen.

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

function StatusChip({ b }) {
  if (b.sent_at) return <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-emerald-500/15 text-emerald-500">sent</span>;
  if (b.send_error) return <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-red-500/15 text-red-500" title={b.send_error}>failed · {b.send_error}</span>;
  return <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-gray-500/15 text-gray-400">stored</span>;
}

// The producer asked for recipients that are not the platform group — ignored, but shown (injection signal).
function OverrideBadge({ b }) {
  if (!b.recipients_overridden || !(b.requested_recipients || []).length) return null;
  return (
    <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-500"
          title={`Producer asked for: ${(b.requested_recipients || []).join(', ')} — ignored; sent to the platform group only`}>
      override
    </span>
  );
}

export default function BriefsPage() {
  const [data, setData] = useState({ briefs: [], types: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [open, setOpen] = useState(null);                       // brief id shown in the viewer
  const [html, setHtml] = useState('');                         // its stored HTML (fetched with the API token; an iframe src can't carry it)

  const load = () => {
    setLoading(true);
    researchosService.getBriefs(typeFilter ? { type: typeFilter } : {})
      .then((r) => { setData(r.data || { briefs: [], types: [] }); setError(''); })
      .catch(() => setError('Could not load briefs.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [typeFilter]);   // eslint-disable-line react-hooks/exhaustive-deps

  const labelFor = useMemo(() => {
    const m = Object.fromEntries((data.types || []).map((t) => [t.type, t.display_name || t.type]));
    return (t) => m[t] || t;
  }, [data.types]);
  const typeOptions = useMemo(
    () => Array.from(new Set([...(data.types || []).map((t) => t.type), ...(data.briefs || []).map((b) => b.type)])).sort(),
    [data],
  );

  const cols = [
    { key: 'brief_date', type: 'string', get: (r) => r.brief_date },
    { key: 'type', type: 'string', get: (r) => r.type },
    { key: 'subject', type: 'string', get: (r) => r.subject },
    { key: 'recipients', type: 'number', get: (r) => (r.recipients || []).length },
    { key: 'received_at', type: 'string', get: (r) => r.received_at || '' },
    { key: 'sent_at', type: 'string', get: (r) => r.sent_at || '' },
    { key: 'source', type: 'string', get: (r) => r.source || '' },
  ];
  const { sorted, toggle, indicator } = useSortableRows(data.briefs || [], cols);
  const current = (data.briefs || []).find((b) => b.id === open);
  useEffect(() => {
    setHtml('');
    if (!open) return;
    researchosService.getBriefHtml(open).then((r) => setHtml(String(r.data || ''))).catch(() => setHtml('<p>Could not load this brief.</p>'));
  }, [open]);

  const sentCount = (data.briefs || []).filter((b) => b.sent_at).length;
  const failedCount = (data.briefs || []).filter((b) => !b.sent_at && b.send_error).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-gray-900 dark:text-gray-100">Courier</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">Daily briefs received from the producer and emailed on arrival to the platform-defined recipient group of each type. A type without a group is stored, never sent.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                  className="bg-transparent border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-gray-800 dark:text-gray-200">
            <option value="">All types</option>
            {typeOptions.map((t) => <option key={t} value={t}>{labelFor(t)}</option>)}
          </select>
          <button type="button" onClick={load} className="border border-gray-300 dark:border-gray-700 rounded px-2 py-1 text-gray-600 dark:text-gray-300">Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {[['Briefs', (data.briefs || []).length, ''], ['Sent', sentCount, 'text-emerald-500'], ['Failed', failedCount, failedCount ? 'text-red-500' : '']].map(([k, v, c]) => (
          <div key={k} className="rounded border border-gray-200 dark:border-gray-800 p-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">{k}</div>
            <div className={`font-serif text-2xl ${c}`}>{v}</div>
          </div>
        ))}
      </div>

      {error && <div className="text-xs text-red-500">{error}</div>}
      {loading && !error && <div className="text-xs text-gray-500">Loading…</div>}

      <div className="overflow-x-auto scrollbar-thin rounded border border-gray-200 dark:border-gray-800">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 dark:bg-gray-900/60 text-[10px] uppercase tracking-wider text-gray-500">
            <tr>
              <SortableTh label="Date" colKey="brief_date" toggle={toggle} indicator={indicator} />
              <SortableTh label="Type" colKey="type" toggle={toggle} indicator={indicator} />
              <SortableTh label="Subject" colKey="subject" toggle={toggle} indicator={indicator} />
              <SortableTh label="To" colKey="recipients" toggle={toggle} indicator={indicator} />
              <SortableTh label="Received" colKey="received_at" toggle={toggle} indicator={indicator} />
              <SortableTh label="Sent" colKey="sent_at" toggle={toggle} indicator={indicator} />
              <SortableTh label="Source" colKey="source" toggle={toggle} indicator={indicator} />
              <th className="px-2 py-1 text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((b) => (
              <tr key={b.id} onClick={() => setOpen(b.id === open ? null : b.id)}
                  className={`cursor-pointer border-t border-gray-100 dark:border-gray-800 ${b.id === open ? 'bg-amber-500/5' : 'hover:bg-gray-50 dark:hover:bg-gray-900/40'}`}>
                <td className="px-2 py-1 whitespace-nowrap tabular-nums">{b.brief_date}</td>
                <td className="px-2 py-1 whitespace-nowrap">{labelFor(b.type)}</td>
                <td className="px-2 py-1 max-w-[28rem] truncate" title={b.subject}>{b.subject}</td>
                <td className="px-2 py-1 whitespace-nowrap" title={(b.recipients || []).join(', ')}>{(b.recipients || []).length}<OverrideBadge b={b} /></td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500">{fmtTs(b.received_at)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500">{fmtTs(b.sent_at)}</td>
                <td className="px-2 py-1 whitespace-nowrap text-gray-500">{b.source || b.producer || '—'}</td>
                <td className="px-2 py-1 whitespace-nowrap"><StatusChip b={b} /></td>
              </tr>
            ))}
            {!loading && !sorted.length && (
              <tr><td colSpan={8} className="px-2 py-6 text-center text-gray-500">No briefs received yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {current && (
        <div className="rounded border border-gray-200 dark:border-gray-800">
          <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-800 text-xs">
            <div className="min-w-0">
              <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{current.subject}</div>
              <div className="text-gray-500 truncate">To: {(current.recipients || []).join(', ') || '— (no platform group, stored only)'}</div>
              {current.recipients_overridden && (current.requested_recipients || []).length > 0 && (
                <div className="text-amber-500 truncate">Producer asked for: {(current.requested_recipients || []).join(', ')} — ignored</div>
              )}
            </div>
            <button type="button" onClick={() => setOpen(null)} className="text-gray-500">Close</button>
          </div>
          {/* stored HTML, rendered as the recipient saw it; sandboxed (no scripts, no navigation) */}
          <iframe title={current.subject} srcDoc={html} sandbox=""
                  className="w-full bg-white" style={{ height: '70vh', border: 0 }} />
        </div>
      )}
    </div>
  );
}
